import type { CombinedError } from '@urql/core';

import { TaxonomyType } from '@revelio/data-access';
import type { TreeData } from '@revelio/ui';

import { useActivityTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useActivityTaxonomyTree';
import { useCustomSkillTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useGeographyTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useGeographyTaxonomyTree';
import { useTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useTaxonomyTree';
import { getGeographyBranchLabels } from '../../../../../../shared/selection-lists/utils/geographyTaxonomyLabels';
import {
  getTaxonomyBranchLabels,
  type TaxonomyLevelMetadata,
} from '../../../../../../shared/selection-lists/utils/taxonomyTree';
import { useFilterContext } from '../../../../filters/contexts/FilterProvider';
import type { FilterSelectionsMap } from '../../../../filters/types/filters';
import { formatColumnName } from '../../../Chart/chart-menu/data-download/converters/utils';
import {
  areStringArraysEqual,
  useStableReference,
} from '../../utils/use-stable-reference';
import type { IcicleTaxonomy } from './taxonomies/types';

type TreeSourceContext = {
  activeFilterMap: FilterSelectionsMap;
  geographySelectionListIds: readonly string[];
  geographyTaxonomyId: number;
  skillTaxonomyId: number | undefined;
  skillTaxonomyVersion: string | undefined;
  activityTaxonomyId: number | undefined;
  activityTaxonomyVersion: string | undefined;
};

type TreeResult = {
  treeData: TreeData[];
  levels: TaxonomyLevelMetadata[];
  fetching: boolean;
  error: CombinedError | undefined;
};

const areLevelIdsEqual = (
  previous: readonly string[] | null,
  next: readonly string[] | null
) =>
  previous === next ||
  (previous !== null && next !== null && areStringArraysEqual(previous, next));

const levelLabelsFor = (
  taxonomy: IcicleTaxonomy,
  levels: readonly TaxonomyLevelMetadata[],
  levelIds: readonly string[] | null
): readonly string[] => {
  if (levelIds === null) return [];
  const labels = (
    taxonomy.tree.kind === 'geography'
      ? getGeographyBranchLabels(levels)
      : getTaxonomyBranchLabels(levels)
  ).map(({ label }) => label);
  return levelIds.map(
    (levelId, depth) => labels[depth] || formatColumnName(levelId)
  );
};

const treeSourceKey = (
  taxonomy: IcicleTaxonomy,
  context: TreeSourceContext
): string => {
  const { tree } = taxonomy;
  switch (tree.kind) {
    case 'reference':
      return `reference:${tree.taxonomyId(context) ?? ''}`;
    case 'geography':
      return `geography:${context.geographyTaxonomyId}`;
    case 'skill':
      return `skill:${context.skillTaxonomyId ?? ''}:${context.skillTaxonomyVersion ?? ''}`;
    case 'activity':
      return `activity:${context.activityTaxonomyId ?? ''}:${context.activityTaxonomyVersion ?? ''}`;
  }
};

/** Identifies the tree an icicle is drawn against, so a taxonomy switch remounts the chart. */
export const useTaxonomyIcicleSourceKey = (taxonomy: IcicleTaxonomy) =>
  treeSourceKey(taxonomy, useFilterContext());

export const useTaxonomyIcicleTree = (
  taxonomy: IcicleTaxonomy,
  pause: boolean
) => {
  const filterContext = useFilterContext();
  const { kind } = taxonomy.tree;
  const reference = taxonomy.tree.kind === 'reference' ? taxonomy.tree : null;
  const activityLevels =
    taxonomy.tree.kind === 'activity' ? taxonomy.tree.levels : undefined;
  const referenceTaxonomyId = reference?.taxonomyId(filterContext);

  const referenceTree = useTaxonomyTree({
    taxonomyType: reference?.taxonomyType ?? TaxonomyType.Role,
    taxonomyId: referenceTaxonomyId,
    levels: reference?.levels,
    priority: 'high',
    pause: pause || reference === null || referenceTaxonomyId === undefined,
  });
  const geographyTree = useGeographyTaxonomyTree({
    taxonomyId: filterContext.geographyTaxonomyId,
    priority: 'high',
    pause: pause || kind !== 'geography',
  });
  const skillTree = useCustomSkillTaxonomyTree(
    filterContext.skillTaxonomyId,
    filterContext.skillTaxonomyVersion,
    { enabled: !pause && kind === 'skill' }
  );
  const activityTree = useActivityTaxonomyTree({
    overrideTaxonomyId: filterContext.activityTaxonomyId,
    taxonomyVersionOverride: filterContext.activityTaxonomyVersion,
    levels: activityLevels,
    enabled: !pause && kind === 'activity',
  });

  const tree: TreeResult = (() => {
    switch (kind) {
      case 'reference':
        return {
          ...referenceTree,
          fetching: referenceTree.fetching || referenceTaxonomyId === undefined,
        };
      case 'geography':
        return geographyTree;
      case 'skill':
        return {
          treeData: skillTree.treeData,
          levels: skillTree.levels,
          fetching: skillTree.isCustomSkillTaxonomyPending,
          error: undefined,
        };
      case 'activity':
        return {
          treeData: activityTree.treeData,
          levels: activityTree.levels,
          fetching: activityTree.isActivityTaxonomyPending,
          error: undefined,
        };
    }
  })();

  const levelIds = useStableReference(
    taxonomy.levelIds(tree.levels, filterContext),
    areLevelIdsEqual
  );
  const levelLabels = useStableReference(
    levelLabelsFor(taxonomy, tree.levels, levelIds),
    areStringArraysEqual
  );

  return {
    treeData: tree.treeData,
    levelIds,
    levelLabels,
    fetching: tree.fetching,
    error: tree.error,
  };
};
